// Envelope-backed address util. Dữ liệu nạp runtime qua AntAddress.ensure(country, API) → cache module-level.
// Envelope: { country, version, mode, levels:[{level,label}], units:[{level,code,parent,name:{vi,en,...}}] }
import AntLocalStorage from '../AntLocalStorage';

const _cache = {}; // country -> envelope
const _loading = {}; // country -> Promise
const _failAt = {}; // country -> mốc thời gian lỗi gần nhất (ms) — cho phép thử lại, xem ensure()
const RETRY_AFTER_MS = 15000; // hỏng thì chờ chừng này rồi mới gọi lại (tránh gọi dồn dập)
const PERSISTENT_CACHE_GROUP = 'SysAddressFile';
const REVALIDATE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
const PERSISTENT_STORAGE_SECONDS = 365 * 24 * 60 * 60;

function _normalizeCountry(country) {
    return String(country || 'VN').trim().toUpperCase() || 'VN';
}

function _persist(country, env) {
    // Serializing a large country envelope must not delay the first address
    // dropdown render. Persistence is best-effort and runs after this turn.
    setTimeout(() => {
        const payload = { envelope: env, validatedAt: Date.now() };
        Promise.resolve(AntLocalStorage.setData(PERSISTENT_CACHE_GROUP, country, payload, PERSISTENT_STORAGE_SECONDS)).catch(() => {});
    }, 0);
}

function _readPersisted(stored) {
    const payload = stored?.data;
    const env = payload?.envelope?.units instanceof Array ? payload.envelope : payload?.units instanceof Array ? payload : null;
    const validatedAt = Number(payload?.validatedAt);
    return {
        env,
        needsValidation:
            stored?.expired === true ||
            !Number.isFinite(validatedAt) ||
            validatedAt <= 0 ||
            Date.now() - validatedAt >= REVALIDATE_AFTER_MS
    };
}

// Báo rõ lý do danh sách rỗng. Trước đây mọi thất bại đều im lặng thành {levels:[],units:[]}
// nên triệu chứng luôn là "ô chọn trống", không thể lần ra nguyên nhân.
function _warn(country, reason, detail) {
    try {
        // eslint-disable-next-line no-console
        console.warn('[AntAddress] Không nạp được dữ liệu địa chỉ nước ' + country + ' — ' + reason, detail || '');
    } catch (e) {
        /* môi trường không có console */
    }
}

export default class AntAddress {
    constructor(country, locale) {
        this.country = _normalizeCountry(country);
        // Chuẩn hoá locale: 'vi-VN' → 'vi' (nhãn/đơn vị trong envelope gắn khóa 'vi'/'en'…),
        // mirror AntLocalization (split('-')[0]); thiếu bước này → tra nhãn['vi-VN'] trượt → rớt tiếng Anh.
        this.locale = String(locale || 'vi').split('-')[0];
    }

    // Nạp envelope 1 nước (idempotent, cache). API = AntAPI instance (có getAddressFile).
    // Thất bại KHÔNG được ghi vào _cache: cache rỗng vĩnh viễn khiến một lần trục trặc mạng/máy chủ
    // là ô chọn trống tới hết phiên. Thay vào đó ghi mốc lỗi để lần gọi sau (quá RETRY_AFTER_MS) thử lại.
    static ensure(country, API) {
        country = _normalizeCountry(country);
        const empty = { country: country, levels: [], units: [] };
        if (_cache[country]) return Promise.resolve(_cache[country]);
        if (_loading[country]) return _loading[country];

        if (!API || !API.getAddressFile) {
            _warn(country, 'chưa truyền API (thiếu getAddressFile) — kiểm tra nơi gọi AntAddress.ensure');
            return Promise.resolve(empty);
        }
        if (_failAt[country] && Date.now() - _failAt[country] < RETRY_AFTER_MS) {
            return Promise.resolve(empty); // vừa lỗi xong, chưa tới lúc thử lại
        }

        _loading[country] = (async () => {
            let persisted = null;
            let needsValidation = true;
            try {
                const stored = await AntLocalStorage.getData(PERSISTENT_CACHE_GROUP, country, { includeMeta: true });
                const persistedState = _readPersisted(stored);
                persisted = persistedState.env;
                needsValidation = persistedState.needsValidation;
            } catch (e) {
                // Persistent cache is optional; continue with the API.
            }

            // A current local copy avoids the API completely across reloads and
            // browser tabs. Address files are versioned and revalidated weekly.
            if (persisted && !needsValidation) {
                delete _failAt[country];
                _cache[country] = persisted;
                return persisted;
            }

            try {
                const response = await API.getAddressFile(country, persisted?.version);
                const env = response && response.data ? response.data : response;
                if (env?.notModified && persisted) {
                    delete _failAt[country];
                    _cache[country] = persisted;
                    _persist(country, persisted); // renew validation TTL
                    return persisted;
                }
                if (env?.units instanceof Array) {
                    delete _failAt[country];
                    _cache[country] = env;
                    _persist(country, env);
                    return env;
                }

                if (persisted) {
                    _cache[country] = persisted; // stale but usable offline/failure fallback
                    _warn(country, 'không kiểm tra được phiên bản mới, tiếp tục dùng dữ liệu đã lưu', (env && env.error) || env);
                    return persisted;
                }

                _failAt[country] = Date.now();
                _warn(country, 'máy chủ trả về không có dữ liệu', (env && env.error) || env);
                return empty;
            } catch (e) {
                if (persisted) {
                    _cache[country] = persisted;
                    _warn(country, 'gọi máy chủ thất bại, tiếp tục dùng dữ liệu đã lưu', (e && e.message) || e);
                    return persisted;
                }
                _failAt[country] = Date.now();
                _warn(country, 'gọi máy chủ thất bại', (e && e.message) || e);
                return empty;
            }
        })().finally(() => {
            delete _loading[country];
        });
        return _loading[country];
    }

    static _setCacheForTest(country, env) {
        _cache[_normalizeCountry(country)] = env;
    }
    static clearCache() {
        Object.keys(_cache).forEach((k) => delete _cache[k]);
        Object.keys(_loading).forEach((k) => delete _loading[k]);
        Object.keys(_failAt).forEach((k) => delete _failAt[k]); // xoá cả mốc lỗi → thử lại ngay
    }

    // Trích cấp hành chính (1/2/3) từ dataField adm{N}_code|adm{N}_name; 0 nếu không phải field adm.
    // Phủ cả prefix (transport_/store_/receiver_…) vì khớp regex cuối chuỗi.
    static admLevelFromField(dataField) {
        var m = String(dataField || '').match(/adm([123])_(code|name)$/);
        return m ? Number(m[1]) : 0;
    }

    _env() {
        return _cache[this.country] || { levels: [], units: [] };
    }
    isLoaded() {
        return !!_cache[this.country];
    }
    getLevelCount() {
        if (!this.isLoaded()) return 3; // chưa load xong → default 3 (transient)
        return (this._env().levels || []).length; // đã load → số cấp thật (0 cho nước postal vd SG)
    }
    getLevelLabel(level) {
        const x = (this._env().levels || []).find((e) => e.level === level);
        return x ? x.label : null;
    }
    // Nhãn cấp đã resolve theo locale (fallback en→vi→''). Cho UI dropdown khỏi hardcode Tỉnh/Quận/Phường.
    getLevelLabelText(level) {
        const lab = this.getLevelLabel(level);
        if (!lab) return '';
        if (typeof lab === 'string') return lab;
        return lab[this.locale] || lab.en || lab.vi || '';
    }
    // Chế độ nhập: 'cascade' (dropdown hành chính) | 'postal' (chỉ ô mã bưu chính, vd SG). Default cascade.
    getMode() {
        return this._env().mode || 'cascade';
    }
    // Tên 1 đơn vị theo (level, code) — để ghi adm{N}_name khi chỉ có code.
    getUnitName(level, code) {
        if (code == null || code === '') return null;
        const u = (this._env().units || []).find((x) => x.level === level && x.code == code);
        return u ? this._nm(u) : null;
    }
    _nm(u) {
        const loc = this.locale;
        return u.name && typeof u.name === 'object' ? u.name[loc] || u.name.vi || u.name.en || u.code : u.name || u.code;
    }
    _units(level, parent) {
        return (this._env().units || [])
            .filter((u) => u.level === level && (parent == null ? u.parent == null : u.parent === parent))
            .map((u) => ({ code: u.code, name: this._nm(u) }))
            .sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    getProvinces() {
        return this._units(1, null);
    }
    getProvince(code) {
        return this._units(1, null).find((u) => u.code == code) || null;
    }
    getDistricts(adm1_code) {
        return this._units(2, adm1_code);
    }
    getDistrict(code) {
        var obj = (this._env().units || []).find((a) => a.level === 2 && a.code == code);
        return obj ? { code: obj.code, name: this._nm(obj) } : null;
    }
    getWards(adm2_code) {
        return this._units(3, adm2_code);
    }
    getWard(code) {
        var obj = (this._env().units || []).find((a) => a.level === 3 && a.code == code);
        return obj ? { code: obj.code, name: this._nm(obj) } : null;
    }
}
